<template>
    <div class="organisation-amount-info">
        <div class="organisation-amount-info__label">
            {{
                isFxAndMethodSelected
                    ? t("organisationHeader.youPayLabel")
                    : hasFxQuotes && cheapestQuoteAvailable
                      ? t("organisationHeader.paymentFrom")
                      : organisationStore.organisation?.organisationName +
                        " " +
                        t("organisationHeader.receivesLabel")
            }}
        </div>
        <div
            class="organisation-amount-info__value"
            :class="{
                'organisation-amount-info__value--stale': isExpiredQuote,
            }"
        >
            {{
                hasFxQuotes &&
                cheapestQuoteAvailable &&
                !paymentsStore.selectedProvider
                    ? formatAmountBasedOnLocaleAndCurrency(
                          language,
                          cheapestQuoteAvailable.localCurrencyCode,
                          cheapestQuoteAvailable.localAmount,
                      )
                    : formatAmountBasedOnLocaleAndCurrency(
                          language,
                          amountDetails.localCurrency,
                          amountDetails.localAmount,
                      )
            }}
        </div>
        <div
            v-if="isFxAndMethodSelected && !isExpiredQuote"
            class="organisation-amount-info__spread"
        >
            {{
                organisationStore.organisation?.organisationName +
                " " +
                t("organisationHeader.receivesLabel")
            }}
            <strong>{{
                formatAmountBasedOnLocaleAndCurrency(
                    language,
                    amountDetails.baseCurrency,
                    amountDetails.baseAmount,
                )
            }}</strong>
        </div>
        <div
            v-if="
                isFxAndMethodSelected &&
                fxInformation.quoteRate &&
                !isExpiredQuote
            "
            class="organisation-amount-info__rate-row"
            data-cy="card-fx-rate-information"
        >
            <span class="organisation-amount-info__rate-label">{{
                t("organisationHeader.exchangeRateLabel")
            }}</span>
            <span
                >1{{ amountDetails.localCurrency }} =
                {{ parseFloat(fxInformation.quoteRate).toFixed(4) }}
                {{ amountDetails.baseCurrency }}</span
            >
        </div>
        <fx-rate-lock-badge
            v-if="
                isFxAndMethodSelected &&
                fxInformation.quoteExpiryDate &&
                smAndUp
            "
            :quote-expiry-date="fxInformation.quoteExpiryDate"
            @expired="handleQuoteExpired"
        />
        <div
            v-if="!isFxAndMethodSelected && countryCode === 'GB'"
            class="organisation-amount-info__spread"
        >
            <button
                type="button"
                class="text-decoration-underline primary-text mt-1"
                data-cy="finance-selina-option"
                @click="isSelinaModalOpen = true"
            >
                {{ t("organisationHeader.selinaCTA") }}
            </button>
        </div>

        <selina-finance-modal
            v-model="isSelinaModalOpen"
            :language="language"
            :local-currency="amountDetails.localCurrency"
            :local-amount="amountDetails.localAmount"
        />
    </div>
</template>

<script setup lang="ts">
import {
    usePaymentsStore,
    useCheckoutStore,
    useOrganisationStore,
} from "@/plugins/store";
import { computed, watch, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";

import { formatAmountBasedOnLocaleAndCurrency } from "@/helpers/amountProcessors";
import fxRateLockBadge from "@/components/layout/fxRateBadge.vue";
import selinaFinanceModal from "@/components/layout/selinaFinanceModal.vue";
import { storeToRefs } from "pinia";
import { PaymentMethods } from "@esenda/core-ui/constants";

const componentProps = defineProps<{
    language: string;
    defaultLocalCurrency: string;
    defaultBaseCurrency: string;
    defaultLocalAmount: string | number;
    defaultBaseAmount: string | number;
    countryCode: string;
}>();

const paymentsStore = usePaymentsStore();
const organisationStore = useOrganisationStore();
const checkoutStore = useCheckoutStore();
const { isExpiredQuote } = storeToRefs(checkoutStore);
const { t } = useI18n();
const { smAndUp } = useDisplay();

const isSelinaModalOpen = ref(false);

const amountDetails = computed(() => {
    const provider = paymentsStore.selectedProviderDetails;

    if (provider) {
        return {
            localAmount: provider.localAmount,
            localCurrency: provider.localCurrencyCode,
            baseAmount: provider.baseAmount,
            baseCurrency: provider.baseCurrencyCode,
        };
    }

    return {
        localAmount: componentProps.defaultLocalAmount,
        localCurrency: componentProps.defaultLocalCurrency,
        baseAmount: componentProps.defaultBaseAmount,
        baseCurrency: componentProps.defaultBaseCurrency,
    };
});

const hasFxQuotes = computed(() => {
    return paymentsStore.paymentMethods?.some(({ isFxMethod }) => isFxMethod);
});

const isFxAndMethodSelected = computed(() => {
    return (
        amountDetails.value.localCurrency !== amountDetails.value.baseCurrency
    );
});

const fxInformation = computed(() => {
    const provider = paymentsStore.selectedProviderDetails;
    if (provider) {
        return {
            quoteRate: provider.quoteRate,
            quoteExpiryDate: provider.quoteExpiryDate,
        };
    }
    return {
        quoteRate: null,
        quoteExpiryDate: null,
    };
});

const cheapestQuoteAvailable = computed(() => {
    return paymentsStore.paymentMethods
        ?.filter(
            ({ paymentMethod, isFxMethod, availability }) =>
                paymentMethod === PaymentMethods.ADYEN &&
                isFxMethod &&
                availability.available,
        )
        .sort((a, b) => Number(a.localAmount) - Number(b.localAmount))[0];
});

const handleQuoteExpired = () => {
    const provider = paymentsStore.selectedProviderDetails;
    if (provider?.paymentMethod && provider?.quoteExpiryDate) {
        checkoutStore.setExpiredQuote({
            provider: provider.paymentMethod,
            quoteExpiryDate: provider.quoteExpiryDate,
        });
    }
};

watch(
    () => fxInformation.value.quoteExpiryDate,
    (expiryUtc) => {
        if (!expiryUtc) {
            checkoutStore.setExpiredQuote(null);
            return;
        }
        const formattedDate = expiryUtc.split(".")[0] + "Z";
        const isExpired = new Date(formattedDate).getTime() <= Date.now();
        if (isExpired) {
            handleQuoteExpired();
        } else {
            checkoutStore.setExpiredQuote(null);
        }
    },
    { immediate: true },
);
</script>

<style scoped>
.organisation-amount-info {
    align-self: stretch;
    margin: 8px -16px -16px;
    padding: 20px 16px 28px;
    background-color: #eff6ff;
    background-image: linear-gradient(to top, white, #eff6ff);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    text-align: center;
    border-top: 1px solid var(--surface-disabled);
    /* border-bottom: 1px solid var(--surface-disabled); */
}

.organisation-amount-info__label {
    font-size: 13px;
    color: var(--text-secondary);
}

.organisation-amount-info__value {
    font-size: 36px;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.1;
}

.organisation-amount-info__spread {
    font-size: 13px;
    color: var(--text-secondary);
}

.organisation-amount-info__spread-value {
    color: var(--action-primary);
    font-weight: 500;
}

.organisation-amount-info__value--stale {
    opacity: 0.35;
    filter: blur(3px);
    user-select: none;
    transition:
        opacity 0.3s ease,
        filter 0.3s ease;
}

.organisation-amount-info__rate-row {
    display: flex;
    gap: 6px;
    font-size: 12px;
    color: var(--text-secondary);
    align-items: center;
}

.organisation-amount-info__rate-label {
    font-weight: 500;
    color: var(--text-label-strong);
}

@media (max-width: 839px) {
    .organisation-amount-info {
        padding: 12px 16px 16px;
        gap: 3px;
    }

    .organisation-amount-info__value {
        font-size: 26px;
    }
}
</style>
